import {
  CreateEquipmentCommand,
  EquipmentDto,
  EquipmentsApi,
  EquipmentType,
  UnassignEquipmentCommand,
  UpdateEquipmentCommand,
} from '@operations/app/api'

import { createApiClient } from '@operations/app/http-client'

import { EventTypes } from '@operations/messages/eventsTypes'
import { equipmentService } from '@operations/services'
import { IEvent, IMessageBus } from '@tom-ui/utils'
import { makeObservable, observable, runInAction } from 'mobx'
import { BaseEntityStore } from './base/BaseEntityStore'

export class EquipmentStore extends BaseEntityStore<EquipmentDto> {
  equipmentTypes: EquipmentType[] = []

  constructor(private messageBus: IMessageBus) {
    super()

    makeObservable(this, {
      equipmentTypes: observable,
    })

    this.messageBus.subscribeEvent<EquipmentDto>(EventTypes.EquipmentUpserted, this.upsertEquipment)

    this.messageBus.subscribeEvent<number>(EventTypes.EquipmentDeleted, this.deleteEquipment)
  }

  async load(includeInactive = false) {
    const equipments = await equipmentService.load({
      includeInactive,
    })

    this.updateStoreItems(equipments)
  }

  async loadOne(id: number) {
    const { data } = await createApiClient(EquipmentsApi).getOne(id)
    this.updateStoreItem(data, data.id)
  }

  async loadPlanned(carrierVisitId: number) {
    const { data } = await createApiClient(EquipmentsApi).getPlanned(carrierVisitId)

    return data
  }

  async add(createEquipmentCommand: CreateEquipmentCommand) {
    await createApiClient(EquipmentsApi).create(createEquipmentCommand)
  }

  async update(updateEquipmentCommand: UpdateEquipmentCommand) {
    await createApiClient(EquipmentsApi).update(updateEquipmentCommand)
  }

  async delete(id: number) {
    await createApiClient(EquipmentsApi)._delete(id)
    this.deleteStoreItem(id)
  }

  async unassign(updateEquipmentCommand: UnassignEquipmentCommand) {
    await createApiClient(EquipmentsApi).unassign(updateEquipmentCommand)
  }

  async loadIdsAlreadyInUseByOtherVisits(vesselId: number) {
    const { data } = await createApiClient(EquipmentsApi).getIdsAlreadyInUseByOtherVisits(vesselId)

    return data
  }

  async loadEquipmentStatistics(currentDate: Date) {
    const { data } = await createApiClient(EquipmentsApi).equipmentStats(currentDate.toUTCString())

    return data
  }

  async loadCraneQueueStats(currentDate: Date) {
    const { data } = await createApiClient(EquipmentsApi).craneQueueStats(currentDate.toUTCString())

    return data
  }

  async loadEquipmentTypes() {
    const { data } = await createApiClient(EquipmentsApi).getEquipmentTypes()

    runInAction(() => {
      this.equipmentTypes = data
    })
  }

  private upsertEquipment = (res: IEvent<EquipmentDto>) => {
    const equipment = res.payload

    if (!equipment) return

    this.updateStoreItem(equipment, equipment.id)
  }

  private deleteEquipment = (res: IEvent<number>) => {
    const id = res.payload

    if (!id) return

    this.deleteStoreItem(id)
  }
}
